/**
 * Shared referral utilities that work on both client and server
 */
import { ReferralStatus } from '@prisma/client';
import { LinkRouter } from '@/code/lib/routes';

/**
 * Generate referral URL - works on both client and server
 * No network requests needed - just string manipulation
 */
export function getReferralUrl(referralCode: string): string {
  let url = LinkRouter.routeForReferral(referralCode);

  // In development, add proxy URL
  if (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_BASE_URL
  ) {
    url += `&developmentProxyUrl=${encodeURIComponent(process.env.NEXT_PUBLIC_BASE_URL)}`;
  }

  return url;
}

interface ReferralStatusDisplay {
  label: string;
  description: (date?: Date) => string;
  color: string;
  icon?:
    | 'mail'
    | 'mail-open'
    | 'user-plus'
    | 'user-check'
    | 'loader'
    | 'square-check';
}
/**
 * Referral status display mapping
 */
export const REFERRAL_STATUS_DISPLAY: Record<
  ReferralStatus,
  ReferralStatusDisplay
> = {
  [ReferralStatus.INVITE_SENT]: {
    label: 'Invite sent',
    description: (date) =>
      date
        ? `Invitation sent - ${date.toLocaleDateString()}`
        : 'Invitation sent',
    color: 'gray',
    icon: 'mail',
  },
  [ReferralStatus.LINK_CLICKED]: {
    label: 'Invite opened',
    description: () => 'Invitation pending',
    color: 'gray',
    icon: 'mail-open',
  },
  [ReferralStatus.APP_VISITED]: {
    label: 'Account created',
    description: () => 'Invitation pending',
    color: 'gray',
    icon: 'user-plus',
  },
  [ReferralStatus.ACCOUNT_CREATED]: {
    label: 'Account started',
    description: () => 'Account setup in progress',
    color: 'green',
    icon: 'user-check',
  },
  [ReferralStatus.REFEREE_PAID]: {
    label: 'Rewards processing',
    description: () => 'Referral completed - rewards processing',
    color: 'amber',
    icon: 'loader',
  },
  [ReferralStatus.REWARDS_SENT]: {
    label: 'Rewards applied',
    description: () => 'Rewarded - Rewards credited to your account',
    color: 'blue',
    icon: 'square-check',
  },
};

/**
 * Get display info for a referral status
 */
export function getReferralStatusDisplay(
  status: ReferralStatus,
  date?: Date,
): ReferralStatusDisplay {
  return (
    REFERRAL_STATUS_DISPLAY[status] || {
      label: status,
      description: () => `Status: ${status}`,
      color: 'gray',
    }
  );
}
